import { Button } from '@/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DeviceVariablesForm from '../IntegrationVariables/DeviceForm';
import { Controller, FieldValues, useWatch } from 'react-hook-form';
import { useEffect, useState, useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { PageTitle } from '@/components/PageTitle';
import { useFetchCustomerQuery } from '@/hooks/customerHooks';
import { useSite } from '@/hooks/siteHooks';
import { toast } from '@/hooks/use-toast';
import SelectIntegrationType from './SelectIntegrationType';
import NewExternalConnectionDevice from './NewExternalConnectionDevice';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateDeviceMut,
  CreateExternalConnectionMut,
  UpdateSecondaryDeviceMut,
  AlertingSetupTypesQuery,
  ConfigureAlertingMut,
  ApplyAlertingConfigMut,
} from '@/lib/api/devices/devices';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { AddDeviceFormProvider, useAddDeviceFormCtx } from './AddDeviceFormProvider';
import { DeviceSelectTable } from './DeviceSelectTable';
import { DeviceDetailsForm } from './DeviceDetailsForm';
import DevicesMap from '../DevicesMap';
import { NetworkSettingsForm } from '@/components/NetworkSettingsForm';
import { AnimatedCompletionIcon, FormStepCollapsible } from '@/components/ui/form-step-collapsible';
import { IntegrationLogs } from '../IntegrationVariables/IntegrationLogs';
import { AxiosError } from 'axios';
import { FormSidebarItem } from '@/components/ui/form-sidebar-item';
import React from 'react';
import { BellRing, Cctv, Check, ChevronDown, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { IntegrationLogLevel, type IntegrationScriptLogDto } from '@/lib/api/types.gen';
import {
  ConfigurationPlan,
  useConfigurationPlanSelection,
  Action,
  ConfigActionGroup,
  ApplyResult,
} from './ConfigurationPlan';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';

export interface SetupAlertsHandle {
  /** true = all applied, false = partial failure, null = server/network error */
  applyConfiguration: () => Promise<boolean | null>;
  refresh: () => void;
  hasChanges: boolean;
  hasAppliedChanges: boolean;
  isPending: boolean;
  isFetching: boolean;
}

interface SetupAlertsSectionProps {
  deviceId: string;
  configStale?: boolean;
  onStateChange?: (state: { hasChanges: boolean; hasAppliedChanges: boolean; isPending: boolean; isFetching: boolean }) => void;
  onRefresh?: () => void;
  onBeforeFetch?: () => Promise<void>;
}

const SetupAlertsSection = forwardRef<SetupAlertsHandle, SetupAlertsSectionProps>(({ deviceId, configStale, onStateChange, onRefresh, onBeforeFetch }, ref) => {
  const [hasAppliedChanges, setHasAppliedChanges] = useState(false);
  const [selectedSetupType, setSelectedSetupType] = useState<string | undefined>(undefined);

  const setupTypesQuery = useQuery({
    ...AlertingSetupTypesQuery(deviceId),
  });

  const supportsAutoConfig = setupTypesQuery.data?.supportsAutoConfig ?? false;
  const setupTypes = useMemo(() => {
    const apiTypes = setupTypesQuery.data?.setupTypes ?? [];
    return [...apiTypes, { value: 'manual', label: 'Manual', description: 'Manually configure your device to send alerts to Watchful', recommended: false }];
  }, [setupTypesQuery.data?.setupTypes]);

  const configureAlertingMut = useMutation({
    ...ConfigureAlertingMut(deviceId),
  });

  const applyAlertingMut = useMutation({
    ...ApplyAlertingConfigMut(deviceId),
    onSuccess(data) {
      const hasErrors = data.results?.some(r => !r.success) ?? false;
      if (!hasErrors) {
        setHasAppliedChanges(true);
      }
    },
    onError(error) {
      toast({
        title: 'Server error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Auto-select recommended/single setup type on load
  useEffect(() => {
    if (!selectedSetupType && setupTypesQuery.data) {
      const autoConfigTypes = setupTypes.filter(t => t.value !== 'manual');
      if (!supportsAutoConfig || autoConfigTypes.length === 0) {
        setSelectedSetupType('manual');
      } else {
        const recommended = autoConfigTypes.find(t => t.recommended);
        const initial = recommended ?? (autoConfigTypes.length === 1 ? autoConfigTypes[0] : undefined);
        if (initial) {
          setSelectedSetupType(initial.value);
          // Camera selection is already saved before this component mounts
          configureAlertingMut.mutate(initial.value);
        }
      }
    }
  }, [setupTypes, setupTypesQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetupTypeChange = async (value: string) => {
    if (value === selectedSetupType || configureAlertingMut.isPending) return;
    setSelectedSetupType(value);
    setHasAppliedChanges(false);
    applyAlertingMut.reset();
    if (value === 'manual') {
      configureAlertingMut.reset();
      return;
    }
    if (onBeforeFetch) await onBeforeFetch();
    configureAlertingMut.mutate(value);
  };

  const applyResults: ApplyResult[] | undefined = applyAlertingMut.data?.results;

  const configGroups: ConfigActionGroup[] = useMemo(() => {
    if (!configureAlertingMut.data?.plan?.groups) return [];
    return configureAlertingMut.data.plan.groups.map((group) => ({
      groupName: group.groupName,
      key: group.key,
      description: group.description,
      warning: group.warning,
      actions: group.actions.map((action) => ({
        field: action.field,
        key: action.key,
        action: Action[action.action as keyof typeof Action],
        currentValue: action.currentValue,
        proposedValue: action.proposedValue,
        description: action.description,
        warning: action.warning,
      })),
    }));
  }, [configureAlertingMut.data]);

  const {
    selectedActions,
    handleSelectionChange,
    handleGroupSelectionChange,
  } = useConfigurationPlanSelection(configGroups);

  const getSelectedChangeIds = useCallback((): string[] => {
    const changeIds: string[] = [];
    configGroups.forEach((group) => {
      const selectedKeys = selectedActions.get(group.groupName);
      if (selectedKeys) {
        group.actions.forEach((action) => {
          if (selectedKeys.has(action.key)) {
            changeIds.push(`${group.key}.${action.key}`);
          }
        });
      }
    });
    return changeIds;
  }, [configGroups, selectedActions]);

  const hasChanges = configGroups.length > 0 && getSelectedChangeIds().length > 0;

  const applyConfiguration = useCallback(async (): Promise<boolean | null> => {
    const changeIds = getSelectedChangeIds();
    if (changeIds.length === 0) return true;
    try {
      const result = await applyAlertingMut.mutateAsync(changeIds);
      const hasErrors = result.results?.some(r => !r.success) ?? false;
      return !hasErrors;
    } catch {
      // Return null (not false) so the caller knows this was a server/network error,
      // not a partial failure. onError already showed the "Server error" toast.
      return null;
    }
  }, [getSelectedChangeIds, applyAlertingMut]);

  const refresh = useCallback(async () => {
    if (selectedSetupType && !configureAlertingMut.isPending) {
      setHasAppliedChanges(false);
      applyAlertingMut.reset();
      if (onBeforeFetch) await onBeforeFetch();
      configureAlertingMut.mutate(selectedSetupType);
      onRefresh?.();
    }
  }, [selectedSetupType, configureAlertingMut, applyAlertingMut, onRefresh, onBeforeFetch]);

  useImperativeHandle(ref, () => ({
    applyConfiguration,
    refresh,
    hasChanges,
    hasAppliedChanges,
    isPending: applyAlertingMut.isPending,
    isFetching: configureAlertingMut.isPending,
  }), [applyConfiguration, refresh, hasChanges, hasAppliedChanges, applyAlertingMut.isPending, configureAlertingMut.isPending]);

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.({ hasChanges, hasAppliedChanges, isPending: applyAlertingMut.isPending, isFetching: configureAlertingMut.isPending });
  }, [hasChanges, hasAppliedChanges, applyAlertingMut.isPending, configureAlertingMut.isPending, onStateChange]);

  // Still fetching — show nothing
  if (setupTypesQuery.isPending) return null;

  const isManualSetup = selectedSetupType === 'manual' || (!supportsAutoConfig && !selectedSetupType);

  // For auto-config integrations: don't render until mutation has started
  if (!isManualSetup && !configureAlertingMut.isPending && !configureAlertingMut.data && !configureAlertingMut.error) return null;

  return (
    <FormStepCollapsible
      id="setup-alerts"
      title="Configure alerts on the connected device"
      isValid={isManualSetup ? false : hasAppliedChanges}
      hasError={false}
      defaultOpen={true}
    >
      <div className="space-y-4 p-6 pt-0">
        {setupTypes.length > 1 && (
          <div className="space-y-2">
            <div className="grid gap-4 sm:grid-cols-3">
              {setupTypes.map((type) => {
                const isSelected = selectedSetupType === type.value;
                const isDisabled = configureAlertingMut.isPending && !isSelected;
                return (
                  <label
                    key={type.value}
                    className={`flex flex-col rounded-lg border p-4 transition-opacity ${
                      isSelected
                        ? 'border-lime bg-lime/10'
                        : 'border-gray-700'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => !isDisabled && handleSetupTypeChange(type.value)}
                  >
                    <div className="flex w-full items-center">
                      <input
                        type="radio"
                        name="setupType"
                        checked={isSelected}
                        onChange={() => {}}
                        className="sr-only"
                      />
                      <span className="font-semibold text-lime">{type.label}</span>
                      {type.recommended && (
                        <Badge variant="secondary" className="ml-2 bg-black text-lime border-lime/20 hover:bg-black/80">
                          Recommended
                        </Badge>
                      )}
                      {isSelected && configureAlertingMut.isPending && (
                        <Loader2 className="ml-2 h-4 w-4 animate-spin text-lime" />
                      )}
                    </div>
                    {type.description && (
                      <div className="mt-2 text-xs text-gray-400">{type.description}</div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {isManualSetup && (
          <div className="flex items-start gap-2 text-yellow-400">
            <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <p className="text-sm">
              {supportsAutoConfig
                ? 'No alerts will be sent through until this device is manually configured.'
                : 'This integration does not support automatic alert configuration. You will need to manually configure this device to send alerts to Watchful.'}
            </p>
          </div>
        )}

        {!isManualSetup && setupTypesQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading configuration options...
          </div>
        )}

        {!isManualSetup && configureAlertingMut.isPending && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Fetching configuration...
          </div>
        )}

        {!isManualSetup && configStale && !configureAlertingMut.isPending && (
          <button
            onClick={refresh}
            className="flex items-center gap-2 text-sm text-red-400 transition-colors hover:text-red-300"
          >
            Device selection has changed, click to refresh
            <RefreshCw className="h-3.5 w-3.5 text-lime" />
          </button>
        )}

        {!isManualSetup && !configStale && configureAlertingMut.data && !configureAlertingMut.isPending && (
          <div className="mt-4 space-y-4">

            {configGroups.length > 0 ? (
              <>
                <p className="text-sm font-medium text-gray-300">Proposed device changes</p>
                <ConfigurationPlan
                  groups={configGroups}
                  selectedActions={selectedActions}
                  onSelectionChange={handleSelectionChange}
                  onGroupSelectionChange={handleGroupSelectionChange}
                  isApplying={applyAlertingMut.isPending}
                  applyResults={applyResults}
                  disabled={hasAppliedChanges}
                />
              </>
            ) : configureAlertingMut.data.success !== false ? (
              <div className={`rounded-lg border p-4 flex items-center gap-3 ${
                hasAppliedChanges
                  ? 'border-green-500/30 bg-green-500/10'
                  : 'border-white/10 bg-white/5'
              }`}>
                {hasAppliedChanges ? (
                  <>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20">
                      <Check className="h-4 w-4 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-400">Device is fully configured</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        All settings match the required configuration. No changes needed.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-500/20">
                      <Check className="h-4 w-4 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">No configuration changes needed</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        The device settings already match the required configuration.
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/80 px-1">
                The device can still be saved and alerting setup manually
              </p>
            )}
          </div>
        )}

        {!isManualSetup && !configStale && configureAlertingMut.data?.success === false && configureAlertingMut.data?.scriptLog && configureAlertingMut.data.scriptLog.length > 0 && (
          <div className="-mx-4">
            <IntegrationLogs logs={configureAlertingMut.data.scriptLog as unknown as import('@/lib/api/types.gen').IntegrationScriptLogDto[]} />
          </div>
        )}
      </div>
    </FormStepCollapsible>
  );
});

interface NewDeviceStatus {
  status: 'ready' | 'incomplete' | 'error';
  message: string;
}

type AddDeviceParams = {
  customerID: string | undefined;
};

interface AddDeviceProps {
  customerId?: string;
  siteId?: string;
  hideNavigation?: boolean;
  onDeviceAdded?: () => void;
}

const AddDeviceInner = ({ customerId, siteId, hideNavigation, onDeviceAdded }: AddDeviceProps) => {
  const { customerID } = useParams<AddDeviceParams>();
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const form = useAddDeviceFormCtx();

  const edgeLinkEnabled = useFeatureFlagEnabled(FeatureFlags.EdgeLink);
  const setupAlertsRef = useRef<SetupAlertsHandle>(null);
  const feedsRef = useRef<HTMLDivElement>(null);
  const setupAlertsContainerRef = useRef<HTMLDivElement>(null);
  const optionalDetailsRef = useRef<HTMLDivElement>(null);
  const saveButtonsRef = useRef<HTMLDivElement>(null);
  const [showSkipConfigDialog, setShowSkipConfigDialog] = useState(false);
  const [showApplyConfigDialog, setShowApplyConfigDialog] = useState(false);
  const [applyStep, setApplyStep] = useState<'confirm' | 'applying' | 'saving' | null>(null);
  const [alertsState, setAlertsState] = useState({ hasChanges: false, hasAppliedChanges: false, isPending: false, isFetching: false });
  const [configStale, setConfigStale] = useState(false);
  const [feedsConfirmed, setFeedsConfirmed] = useState(false);
  const deviceConfigRef = useRef<HTMLDivElement>(null);

  // Use props if provided, otherwise fall back to URL parameters
  const [currentCustomer, setCurrentCustomer] = useState<string>(customerId || customerID || '');
  const [currentSite, setCurrentSite] = useState<string>(
    siteId || searchParams.get('siteId') || ''
  );
  const { data: customer, error: customerLookupError } = useFetchCustomerQuery({
    customerID: currentCustomer,
  });

  // Fetch full site details to get address information
  const { data: siteData } = useSite({
    siteId: currentSite,
    companyId: currentCustomer,
    enabled: Boolean(currentSite && currentCustomer),
  });

  const createDeviceMut = useMutation({
    ...CreateDeviceMut(),
  });
  const createExternalConnectionMut = useMutation({
    ...CreateExternalConnectionMut(),
  });
  const latestRes = useMemo(() => {
    if (createDeviceMut.submittedAt <= 0 && createExternalConnectionMut.submittedAt <= 0) return;

    const latest = [createDeviceMut, createExternalConnectionMut].sort(
      (a, b) => b.submittedAt - a.submittedAt
    )[0];

    return latest;
  }, [createDeviceMut, createExternalConnectionMut]);
  const { primaryDevice, subDevices } = latestRes?.data ?? {};
  const errorScriptLog = useMemo(() => {
    if (!(latestRes?.error instanceof AxiosError)) return undefined;

    const logs = latestRes.error.response?.data?.scriptLog;
    if (logs && logs.length > 0) return logs;

    return [
      {
        level: IntegrationLogLevel.Error,
        message: 'Failed to connect to cameras: ' + (latestRes.error.message || 'Unknown error'),
        timestamp: new Date().toISOString(),
      },
    ] as IntegrationScriptLogDto[];
  }, [latestRes?.error]);
  const dbId = useRef<string>();
  const setupTypesQuery = useQuery({
    ...AlertingSetupTypesQuery(dbId.current ?? ''),
    enabled: !!dbId.current,
  });
  const supportsAutoConfig = setupTypesQuery.data?.supportsAutoConfig ?? false;

  const updateSecondaryDeviceMut = useMutation({
    ...UpdateSecondaryDeviceMut(dbId.current ?? ''),
    onError(error) {
      toast({
        title: 'Failed to start monitoring',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const deviceType = useWatch({ control: form.control, name: 'deviceType' });
  const address = useWatch({ control: form.control, name: 'address' });
  const integrationVariables = useWatch({ control: form.control, name: 'integrationVariables' });
  const routeNamespaceId = useWatch({ control: form.control, name: 'routeNamespaceId' });
  const monitoredDevices = useWatch({ control: form.control, name: 'monitoredDevices' });
  const isRelayDevice = deviceType && 'integration' in deviceType && deviceType.integration.integrationType === 'Relay';
  const isSpeakerDevice = deviceType && 'integration' in deviceType && deviceType.integration.integrationType === 'Speaker';
  const connectionError = useMemo(() => {
    if (currentSite == null) return 'No site selected';
    if (latestRes?.data != null && primaryDevice == null) {
      return 'Integration failed to enumerate devices';
    }
    return;
  }, [currentSite, primaryDevice, latestRes]);
  const camerasPending = createDeviceMut.isPending || createExternalConnectionMut.isPending;
  const deviceName = useWatch({ control: form.control, name: 'deviceName' });
  const serialNumber = useWatch({ control: form.control, name: 'serialNumber' });
  const description = useWatch({ control: form.control, name: 'description' });
  const selectedSubDeviceCount = useMemo(() => {
    if (!subDevices) return 0;
    return subDevices.filter((item) => {
      if (!item.id) return true;
      // Check form state first (defaultValue is true in the Controller)
      return monitoredDevices?.[item.id] ?? true;
    }).length;
  }, [subDevices, monitoredDevices]);
  const hasIntegrationValues = Boolean(
    integrationVariables && Object.keys(integrationVariables).length > 0
  );
  const hasSelectedSubDevices = selectedSubDeviceCount > 0;

  // Section validation states using form validation
  const { errors, touchedFields } = form.formState;

  const deviceDetailsValid =
    Boolean(touchedFields.deviceName) &&
    !errors.deviceName &&
    Boolean(deviceName);

  const locationValid = Boolean(currentCustomer && currentSite);

  const connectionSettingsValid =
    Boolean(touchedFields.deviceType) &&
    !errors.deviceType &&
    Boolean(deviceType && hasIntegrationValues);

  // Check for section-specific errors
  const deviceDetailsHasError = Boolean(errors.deviceName);
  const connectionSettingsHasError = Boolean(errors.deviceType);

  useEffect(() => {
    if (customerID) {
      setCurrentCustomer(customerID);
    }
  }, [customerID]);

  const scrollToRef = useCallback((ref: React.RefObject<HTMLElement | null>) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // Scroll to device configuration when device model is selected
  useEffect(() => {
    if (deviceType && deviceConfigRef.current) {
      scrollToRef(deviceConfigRef);
    }
  }, [deviceType, scrollToRef]);

  // Mark alerting config as stale when camera selection changes (only after feeds confirmed)
  const monitoredInitialised = useRef(false);
  useEffect(() => {
    if (!feedsConfirmed || !subDevices || !monitoredDevices) return;
    if (!monitoredInitialised.current) {
      monitoredInitialised.current = true;
      return;
    }
    setConfigStale(true);
  }, [monitoredDevices, subDevices, feedsConfirmed]);

  // Scroll to available feeds when devices load
  useEffect(() => {
    if (subDevices && feedsRef.current) {
      scrollToRef(feedsRef);
    }
  }, [subDevices, scrollToRef]);

  // Prefill address when site data is available or when site changes
  // Only set if user hasn't manually touched the address field
  useEffect(() => {
    if (siteData?.address && currentSite && !touchedFields.address) {
      form.setValue('address', siteData.address);
    }
  }, [currentSite, siteData?.address, form, touchedFields.address]);

  // Prefill routeNamespaceId when site data is available
  useEffect(() => {
    if (siteData?.defaultRouteNamespaceId && currentSite && !touchedFields.routeNamespaceId) {
      form.setValue('routeNamespaceId', siteData.defaultRouteNamespaceId, { shouldTouch: true });
    }
  }, [currentSite, siteData?.defaultRouteNamespaceId, form, touchedFields.routeNamespaceId]);

  // Handle auto-advancing feeds if auto config is not supported
  useEffect(() => {
    if (!feedsConfirmed && !setupTypesQuery.isPending && supportsAutoConfig === false && subDevices && !isRelayDevice && !isSpeakerDevice) {
      if (subDevices.length && dbId.current && !updateSecondaryDeviceMut.isPending) {
        setFeedsConfirmed(true);
      }
    }
  }, [feedsConfirmed, setupTypesQuery.isPending, supportsAutoConfig, subDevices, isRelayDevice, isSpeakerDevice, updateSecondaryDeviceMut.isPending]);

  const status = useMemo<NewDeviceStatus>(() => {
    if (connectionError) {
      return {
        status: 'error',
        message: connectionError,
      };
    }

    if (!(currentSite && deviceType && (isRelayDevice || isSpeakerDevice || !subDevices || (deviceName && serialNumber)))) {
      return {
        status: 'incomplete',
        message: 'Please fill out all fields to continue.',
      };
    }

    if (!isRelayDevice && !isSpeakerDevice && (!subDevices || (!feedsConfirmed && dbId.current))) {
      return {
        status: 'incomplete',
        message: 'Please continue through the feed selection to proceed.',
      };
    }

    return {
      status: 'ready',
      message: 'All fields are filled out. Ready to publish.',
    };
  }, [connectionError, currentSite, deviceType, subDevices, deviceName, serialNumber, isRelayDevice, isSpeakerDevice, feedsConfirmed]);

  const submitIntegrationVariables = async (integrationValues: FieldValues) => {
    if (!deviceType) {
      return;
    }

    if (!currentSite) {
      toast({
        title: 'No site selected',
        description: 'Please select a site to continue',
        variant: 'destructive',
      });
      return;
    }

    const common = {
      id: dbId.current,
      name: deviceName,
      serialNumber: serialNumber,
      description: description,
      customerId: currentCustomer,
      projectId: currentSite,
      address: address ?? {},
      RouteNamespaceId: form.getValues('routeNamespaceId') || null,
    };

    const { id, primaryDevice } =
      'integration' in deviceType
        ? await createDeviceMut.mutateAsync({
          ...common,
          integrationId: deviceType.integration.id,
          fields: integrationValues as FieldValues,
        })
        : await createExternalConnectionMut.mutateAsync({
          ...common,
          externalConnectionId: deviceType.externalConnection,
          deviceCreationChoices: integrationValues,
        });

    if (id != '00000000-0000-0000-0000-000000000000') {
      dbId.current = id;
    } else {
      dbId.current = undefined;
    }

    // Reset alerting-related state on reconnection
    setFeedsConfirmed(false);
    setConfigStale(false);
    setAlertsState({ hasChanges: false, hasAppliedChanges: false, isPending: false, isFetching: false });

    // Scroll to the next relevant section after connecting
    // Feeds section will appear for camera devices, setup alerts for others
    setTimeout(() => {
      if (feedsRef.current) {
        scrollToRef(feedsRef);

        // If 'supportsAutoConfig' is known false, maybe we shouldn't force them to click continue.
        // Wait, they still need to review the feed list if they want. 
        // We will leave the scroll as is here.
      } else if (setupAlertsContainerRef.current) {
        scrollToRef(setupAlertsContainerRef);
      } else if (saveButtonsRef.current) {
        scrollToRef(saveButtonsRef);
      }
    }, 200);

    if (primaryDevice == null) return;

    const values = form.getValues();

    if (values.deviceName === '') {
      form.setValue('deviceName', primaryDevice?.name ?? '');
    }
    if (values.serialNumber === '') {
      form.setValue('serialNumber', primaryDevice?.externalId ?? '');
    }
    if (values.description === '') {
      form.setValue('description', primaryDevice?.description ?? '');
    }
  };

  const startMonitoringCameras = async () => {
    if (!deviceName) {
      toast({
        title: 'Failed to start monitoring',
        description: 'Please add a device name to continue',
        variant: 'destructive',
      });
      return;
    }

    if (!integrationVariables || (!isRelayDevice && !isSpeakerDevice && !primaryDevice) || !dbId) {
      toast({
        title: 'Failed to start monitoring',
        description: 'Fill out Integration Variables',
        variant: 'destructive',
      });
      return;
    }
    await submitIntegrationVariables(integrationVariables);

    if (connectionError) {
      return;
    }

    const { monitoredDevices } = form.getValues();

    try {
      await updateSecondaryDeviceMut.mutateAsync({
        primaryDeviceId: dbId.current,
        devices:
          subDevices?.map((device) => {
            return {
              id: device.id,
              monitored: device.id ? Boolean(monitoredDevices[device.id]) : false,
              capabilities: device.capabilities,
              quillNotes: '',
            };
          }) ?? [],
      });

      if (onDeviceAdded) {
        onDeviceAdded();
      } else {
        navigate(`/devices/${dbId.current}/edit`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (customerLookupError) {
    console.error('Error fetching customer data:', customerLookupError);
  }

  return (
    <Form {...form}>
      <div>
        <PageTitle title="Add Device" />
        {!hideNavigation && (
          <div className="sticky top-14 z-[20] flex items-center justify-between border-b bg-[#111]" data-onboarding="add-device-header">
            <div className="flex items-center text-white p-4">
              <Cctv className="h-6 w-6" />
              <h1 className="mx-4 font-semibold">Add Device</h1>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <span>{customer?.name}</span>
                {siteData?.name && (
                    <>
                        <span>/</span>
                        <span>{siteData.name}</span>
                    </>
                )}
              </div>
            </div>
          </div>
        )}
        <section className="mx-auto my-8 w-full px-5 text-white">
          <div className="mx-auto mt-8 grid w-full max-w-[1400px] grid-cols-[320px_1fr] gap-6">
            <div className="space-y-2 p-4 h-fit" data-onboarding="add-device-sidebar">
              <FormSidebarItem
                href="#connection-settings"
                title="Device Type"
                isActive={Boolean(currentSite && !deviceType)}
                isCompleted={Boolean(deviceType)}
              />

              {subDevices && !isRelayDevice && !isSpeakerDevice && (
                <FormSidebarItem
                  href="#available-feeds"
                  title="Select Feeds"
                  isActive={Boolean(deviceType && !hasSelectedSubDevices)}
                  isCompleted={hasSelectedSubDevices}
                />
              )}

              {dbId.current && (feedsConfirmed || isRelayDevice || isSpeakerDevice) && supportsAutoConfig !== false && (
                <FormSidebarItem
                  href="#setup-alerts"
                  title="Configure Alerting"
                  isActive={!alertsState.hasAppliedChanges}
                  isCompleted={alertsState.hasAppliedChanges}
                />
              )}

              <FormSidebarItem
                href="#device-details"
                title="Device Details"
                isActive={!deviceName}
                isCompleted={Boolean(deviceName)}
              />
            </div>

            <div className="space-y-6">
              <FormStepCollapsible
                        id="connection-settings"
                        title="What type of device are you connecting?"
                        isValid={connectionSettingsValid}
                        hasError={connectionSettingsHasError}
                        collapsedContent={
                        deviceType && (
                            <>
                            {'integration' in deviceType
                                ? deviceType.integration.name
                                : 'External Connection'}
                            </>
                        )
                        }
                        data-onboarding="add-device-connection-section"
                    >
                        <div className="p-6 pt-0">
                        <div className="mb-4">
                            <Controller
                            control={form.control}
                            name="deviceType"
                            render={({ field }) => {
                                return (
                                <SelectIntegrationType
                                    {...field}
                                    onChange={(e) => {
                                    field.onChange(e);
                                    form.setValue('integrationVariables', {});
                                    setFeedsConfirmed(false);
                                    }}
                                />
                                );
                            }}
                            />
                        </div>

                        </div>
                    </FormStepCollapsible>

                    {deviceType && (
                        <div ref={deviceConfigRef}>
                            {edgeLinkEnabled && 'integration' in deviceType && (
                                <div className="mb-4">
                                    <NetworkSettingsForm
                                        siteDefaultRouteId={siteData?.defaultRouteNamespaceId}
                                        showSetSiteDefault={Boolean(currentSite)}
                                        customerId={currentCustomer}
                                        siteId={currentSite}
                                    />
                                </div>
                            )}
                            {'integration' in deviceType ? (
                                <DeviceVariablesForm
                                    key={deviceType.integration.id}
                                    integration={deviceType.integration}
                                    onVariableSubmit={submitIntegrationVariables}
                                    onFormChange={(values) => form.setValue('integrationVariables', values)}
                                    currentlyProcessing={camerasPending}
                                    logs={errorScriptLog ?? null}
                                    routeNamespaceId={routeNamespaceId}
                                />
                            ) : (
                                <NewExternalConnectionDevice
                                    key={deviceType.externalConnection}
                                    id={deviceType.externalConnection}
                                    onChange={(values) => form.setValue('integrationVariables', values)}
                                    onSubmit={submitIntegrationVariables}
                                    values={integrationVariables ?? {}}
                                    processing={camerasPending}
                                    logs={errorScriptLog ?? null}
                                />
                            )}
                        </div>
                    )}

                    {subDevices && !isRelayDevice && !isSpeakerDevice && (
                        <div
                            ref={feedsRef}
                            className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-4"
                            data-onboarding="add-device-feeds-section"
                        >
                            <DeviceSelectTable subDevices={subDevices} />
                            {!feedsConfirmed && setupTypesQuery.isPending && (
                                <div className="flex justify-center py-2" data-testid="alerting-setup-loading">
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                </div>
                            )}
                            {!feedsConfirmed && !setupTypesQuery.isPending && supportsAutoConfig === true && (
                                <div className="flex flex-col items-center gap-2 py-2">
                                    <Button
                                        onClick={async () => {
                                            // Save camera selection to backend before proceeding
                                            if (dbId.current && subDevices) {
                                                const { monitoredDevices } = form.getValues();
                                                await updateSecondaryDeviceMut.mutateAsync({
                                                    primaryDeviceId: dbId.current,
                                                    devices: subDevices.map((device) => ({
                                                        id: device.id,
                                                        monitored: device.id ? Boolean(monitoredDevices?.[device.id]) : false,
                                                        capabilities: device.capabilities,
                                                        quillNotes: '',
                                                    })),
                                                });
                                            }
                                            setFeedsConfirmed(true);
                                            setTimeout(() => {
                                                if (setupAlertsContainerRef.current) {
                                                    scrollToRef(setupAlertsContainerRef);
                                                } else if (saveButtonsRef.current) {
                                                    scrollToRef(saveButtonsRef);
                                                }
                                            }, 200);
                                        }}
                                        disabled={updateSecondaryDeviceMut.isPending}
                                        className="rounded-full bg-[#Dbf133] px-8 py-2.5 text-black font-semibold shadow-[0_0_20px_rgba(219,241,51,0.35)] hover:shadow-[0_0_28px_rgba(219,241,51,0.55)] hover:bg-[#Dbf133]/90 transition-all duration-200"
                                    >
                                        {updateSecondaryDeviceMut.isPending ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Saving selection...
                                            </>
                                        ) : (
                                            <>
                                                <BellRing className="h-4 w-4" />
                                                Configure Alerting
                                            </>
                                        )}
                                    </Button>
                                    <ChevronDown className="h-5 w-5 text-gray-500 animate-bounce" />
                                </div>
                            )}
                        </div>
                    )}

                    {(feedsConfirmed || isRelayDevice || isSpeakerDevice) && dbId.current && supportsAutoConfig !== false && (
                        <div ref={setupAlertsContainerRef}>
                            <SetupAlertsSection
                                ref={setupAlertsRef}
                                deviceId={dbId.current}
                                configStale={configStale}
                                onStateChange={setAlertsState}
                                onRefresh={() => setConfigStale(false)}
                                onBeforeFetch={async () => {
                                    if (!dbId.current || !subDevices) return;
                                    const { monitoredDevices } = form.getValues();
                                    await updateSecondaryDeviceMut.mutateAsync({
                                        primaryDeviceId: dbId.current,
                                        devices: subDevices.map((device) => ({
                                            id: device.id,
                                            monitored: device.id ? Boolean(monitoredDevices?.[device.id]) : false,
                                            capabilities: device.capabilities,
                                            quillNotes: '',
                                        })),
                                    });
                                }}
                            />
                        </div>
                    )}

                    {dbId.current && (feedsConfirmed || isRelayDevice || isSpeakerDevice || !subDevices) && (
                      <div id="device-details" className="space-y-4 rounded-xl border bg-card2 p-6" data-onboarding="add-device-name-section">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="mt-1">
                                <AnimatedCompletionIcon isValid={Boolean(deviceName)} hasError={Boolean(errors.deviceName)} />
                            </div>
                            <FormLabel className="text-xl font-semibold">Name your device</FormLabel>
                        </div>
                        <FormField
                          control={form.control}
                          name="deviceName"
                          render={({ field, fieldState }) => {
                            return (
                              <FormItem className="flex-1">
                                <FormControl>
                                  <Input
                                    id="deviceName"
                                    placeholder="Front Door Camera"
                                    {...field}
                                    className={fieldState.error ? 'border-red-500' : ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>
                    )}

                    {latestRes?.data && (feedsConfirmed || isRelayDevice || isSpeakerDevice || !subDevices) && (
                        <div ref={optionalDetailsRef}>
                        <FormStepCollapsible
                            id="additional-details"
                            title="Optional Details"
                            isValid={deviceDetailsValid && locationValid}
                            hasError={deviceDetailsHasError}
                            defaultOpen={false}
                            icon="optional"
                            description={
                            <span className="block text-sm text-gray-400">
                                {[serialNumber, address?.addressLine1, address?.city].filter(Boolean).join(', ')}
                            </span>
                            }
                            data-onboarding="add-device-additional-details-section"
                        >
                            <div className="space-y-6">
                                <DeviceDetailsForm />

                                <div className="space-y-4 pt-4 border-t border-white/10">
                                    <div className="grid w-full gap-4">
                                        <FormField
                                            control={form.control}
                                            name="address"
                                            render={({ field }) => {
                                                return <DevicesMap address={field.value} setAddress={field.onChange} />;
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </FormStepCollapsible>
                        </div>
                    )}

                    {dbId.current && (feedsConfirmed || isRelayDevice || isSpeakerDevice || !subDevices) && <div ref={saveButtonsRef} className="flex items-center justify-end gap-4" data-onboarding="add-device-confirm-section">
                        {status.status === 'error' && (
                            <span className="text-sm text-red-400">{status.message}</span>
                        )}
                        <Button
                            onClick={() => {
                                if (alertsState.hasChanges && !alertsState.hasAppliedChanges) {
                                    setShowSkipConfigDialog(true);
                                } else {
                                    startMonitoringCameras();
                                }
                            }}
                            disabled={status.status !== 'ready' || updateSecondaryDeviceMut.isPending || alertsState.isFetching}
                            variant={alertsState.hasChanges && !alertsState.hasAppliedChanges ? 'outline' : 'default'}
                            className={
                                alertsState.hasChanges && !alertsState.hasAppliedChanges
                                    ? 'px-8'
                                    : 'bg-[#Dbf133] px-8 text-black hover:bg-[#Dbf133]/90'
                            }
                        >
                            {updateSecondaryDeviceMut.isPending ? 'Saving...' : 'Save device'}
                        </Button>
                        {alertsState.hasChanges && !alertsState.hasAppliedChanges && !configStale && (
                            <Button
                                onClick={() => { setApplyStep('confirm'); setShowApplyConfigDialog(true); }}
                                disabled={
                                    status.status !== 'ready' ||
                                    updateSecondaryDeviceMut.isPending ||
                                    alertsState.isPending ||
                                    alertsState.isFetching
                                }
                                className="bg-[#Dbf133] px-8 text-black hover:bg-[#Dbf133]/90"
                            >
                                {alertsState.isPending
                                    ? 'Applying...'
                                    : updateSecondaryDeviceMut.isPending
                                    ? 'Saving...'
                                    : 'Apply & Save Device'}
                            </Button>
                        )}
                    </div>}

                    <Dialog open={showSkipConfigDialog} onOpenChange={setShowSkipConfigDialog}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Skip alerting configuration?</DialogTitle>
                                <DialogDescription>
                                    There are pending alerting configuration changes that have not been applied to this device.
                                    Saving without applying may mean the device does not send alerts correctly.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowSkipConfigDialog(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={() => {
                                        setShowSkipConfigDialog(false);
                                        startMonitoringCameras();
                                    }}
                                >
                                    Save without applying
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog
                        open={showApplyConfigDialog}
                        onOpenChange={(open) => {
                            if (!open && applyStep && applyStep !== 'confirm') return;
                            setShowApplyConfigDialog(open);
                            if (!open) setApplyStep(null);
                        }}
                    >
                        <DialogContent closable={!applyStep || applyStep === 'confirm'} showCloseButton={!applyStep || applyStep === 'confirm'}>
                            <DialogHeader>
                                <DialogTitle>Confirm Configuration Changes</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to apply these configuration changes? This will modify settings directly on your device.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <div className="flex flex-col gap-2 w-full">
                                    <Button
                                        variant="default"
                                        disabled={!!applyStep && applyStep !== 'confirm'}
                                        onClick={async () => {
                                            setApplyStep('applying');
                                            const success = await setupAlertsRef.current?.applyConfiguration();
                                            if (success) {
                                                setApplyStep('saving');
                                                await startMonitoringCameras();
                                                setShowApplyConfigDialog(false);
                                                setApplyStep(null);
                                            } else {
                                                setShowApplyConfigDialog(false);
                                                setApplyStep(null);
                                                if (success === false) {
                                                    toast({
                                                        title: 'Configuration failed',
                                                        description: 'Some changes could not be applied. Review the errors below before saving.',
                                                        variant: 'destructive',
                                                    });
                                                }
                                            }
                                        }}
                                    >
                                        {applyStep && applyStep !== 'confirm' ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {applyStep === 'applying' ? 'Applying...' : 'Saving...'}
                                            </>
                                        ) : (
                                            'Apply & Save'
                                        )}
                                    </Button>
                                    {applyStep && applyStep !== 'confirm' && (
                                        <p className="text-xs text-gray-400 text-center">
                                            {applyStep === 'applying'
                                                ? 'Updating alerting configuration on device...'
                                                : 'Saving device...'}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    disabled={!!applyStep && applyStep !== 'confirm'}
                                    onClick={() => setShowApplyConfigDialog(false)}
                                >
                                    Cancel
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

            </div>
          </div>
        </section>
      </div>
    </Form>
  );
};

const AddDevice = (props: AddDeviceProps = {}) => {
  return (
    <AddDeviceFormProvider>
      <AddDeviceInner {...props} />
    </AddDeviceFormProvider>
  );
};

export default AddDevice;